import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, cn, Text } from 'rizzui';
import { toast } from 'sonner';
import CustomSelect from '../../../../../components/forms/CustomSelect';
import ModalForm from '../../../../../components/forms/ModalForm';
import CustomTable from '../../../../../components/table/CustomTable';
import useUserNetworks from '../../../../../hooks/api-calls/useUserNetworks';
import useFetch from '../../../../../hooks/useFetch';
import useFilters from '../../../../../hooks/useFilters';
import useHandleErrors from '../../../../../hooks/useHandleErrors';
import Page from '../../../../../layout/Page/Page';
import { ClientService } from '../../../../../services/client/clientService';
import { ClientApiResponse } from '../../../../../type/entities/client-type';
import ClientProfileLayout from '../../ClientProfileLayout';

interface ClientCommercialNetworksProps { }

const columnOrder = [
    'id',
    'commercialNetwork',
    'createdAt',
];

const ClientCommercialNetworks: React.FC<ClientCommercialNetworksProps> = () => {

    const { id = '' } = useParams<{ id: string }>();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters();
    const { handleErrors } = useHandleErrors();

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedNetworks, setSelectedNetworks] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await (new ClientService()).listClientCommercialNetworks({ ...filters, filter_filters: { ...filters.filter_filters, client: id } });
        return response.getResponseData() as ClientApiResponse;
    }, [id, filters]));

    const { getUserNetworksList } = useUserNetworks({ active: true });
    const userNetworks = getUserNetworksList();
    const networksLoading = false;

    const handleSubmit = async () => {
        if (!selectedNetworks.length) {
            toast.error('Debe seleccionar al menos una red comercial');
            return;
        }

        setIsSubmitting(true);
        try {
            await (new ClientService()).addClientCommercialNetwork({
                clientId: id!,
                commercialNetworkIds: selectedNetworks,
            });

            toast.success('Redes comerciales añadidas correctamente');
            setIsModalOpen(false);
            setSelectedNetworks([]);
            refetch();
        } catch (error: any) {
            toast.error(error.message || 'Error al añadir las redes comerciales');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (ids: string[]) => {
        const networksIds = ids.map(id => data?.data.find((item: any) => item.id === id)?.commercialNetwork?.id).filter((id: string | undefined): id is string => !!id);
        const response = await (await (new ClientService()).removeClientCommercialNetwork({ clientId: id, commercialNetworkIds: networksIds })).getResponseData();
        if (response.success) {
            toast.success('Redes comerciales eliminadas correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedNetworks([]);
    };

    // Filtrar redes ya asignadas al cliente
    const assignedNetworkIds = data?.data?.map((item: any) => item.commercialNetwork?.id) || [];
    const availableNetworks = userNetworks?.filter(
        (network: any) => !assignedNetworkIds.includes(network.value)
    ) || [];

    return (
        <ClientProfileLayout isLoading={loading}>
            <Page container='fluid' className='mt-5'>
                {data && (
                    <CustomTable
                        id={'changelogs-table'}
                        columnOrder={columnOrder}
                        data={data?.data}
                        isLoading={loading}
                        columnsNotShown={['client', 'company']}
                        overrideColumns={[
                            {
                                key: 'commercialNetwork',
                                render: (row: any) => {
                                    const cn = row.row.original.commercialNetwork;
                                    return (
                                        <div className="flex items-center gap-3">
                                            <Text>{cn.name} - {cn.code}</Text>
                                        </div>
                                    )
                                },
                            },
                        ]}
                        handleMultipleDelete={handleDelete}
                        filters={filters}
                        updateFilters={updateFilters}
                        updateFilterOrder={updateFilterOrder}
                        defaultOrder={filters.filter_order || undefined}
                        paginationData={{
                            pageSize: filters.limit,
                            currentPage: filters.page,
                            pageCount: data ? data.lastPage : 1,
                            totalCount: data?.totalRegisters,
                            handlePagination: updatePage,
                            handlePerPage: updatePageSize,
                        }}
                        toggleFilters={() => setOpenFilters(!openFilters)}
                    >
                        <Button
                            onClick={() => setIsModalOpen(true)}
                            color="primary"
                            disabled={!availableNetworks.length}
                            className={cn(['mt-4 md:mt-0'])}
                            size="sm"
                        >
                            Añadir Redes Comerciales
                        </Button>
                    </CustomTable>
                )}

                <ModalForm
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    title="Añadir Redes Comerciales"
                    size="md"
                >
                    <div className="space-y-6 pb-6">
                        <div className="text-sm text-gray-600 mb-4">
                            Selecciona las redes comerciales que deseas asignar al cliente.
                        </div>

                        <CustomSelect
                            id="commercial-networks"
                            label="Redes Comerciales Disponibles"
                            isMulti
                            isSearchable
                            placeholder="Seleccionar redes comerciales..."
                            options={availableNetworks}
                            value={availableNetworks.filter((network: any) =>
                                selectedNetworks.includes(network.value)
                            )}
                            onChange={(selectedOptions: any) => {
                                const networkIds = selectedOptions
                                    ? selectedOptions.map((option: any) => option.value)
                                    : [];
                                setSelectedNetworks(networkIds);
                            }}
                            isLoading={networksLoading}
                            noOption={() => 'No hay redes comerciales disponibles'}
                        />

                        {!availableNetworks.length && !networksLoading && (
                            <div className="text-sm text-gray-500 italic">
                                Todas las redes comerciales disponibles ya están asignadas a este cliente.
                            </div>
                        )}

                        <div className="flex justify-end space-x-3 pt-4">
                            <Button
                                variant="outline"
                                onClick={handleCloseModal}
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </Button>
                            <Button
                                color="primary"
                                onClick={handleSubmit}
                                disabled={!selectedNetworks.length || isSubmitting}
                                isLoading={isSubmitting}
                            >
                                {isSubmitting ? 'Añadiendo...' : 'Añadir Redes'}
                            </Button>
                        </div>
                    </div>
                </ModalForm>
            </Page>
        </ClientProfileLayout>
    );
};

export default ClientCommercialNetworks;